import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Search, ShieldCheck, UserPlus, X, UserCheck, UserX, LogOut, KeyRound, Fingerprint,
} from 'lucide-react'
import {
  Modal, Select, ToastProvider, useToast, apiGet, adminMutate, usePermissions,
} from './primitives.js'
import { Checkbox, Field, TextInput } from './form.js'
import { SettingsCrumb } from './SettingsCrumb.js'
import { useSaveBar, guardedClose } from '../shell/editing.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { navigate } from '../router.js'
import { createPortal } from 'react-dom'

/**
 * /settings/users (LOT 5.5.1) — liste, création, MULTI-SÉLECTION.
 *
 * · Cliquer une ligne ouvre la fiche pleine page (/settings/users/:id).
 * · La création vit dans une modale LIVE CONTROL ; l'action finale passe
 *   par la Save Bar globale, contextualisée « Créer le compte ». L'admin ne
 *   choisit JAMAIS le mot de passe : compte en attente d'activation.
 * · Les cases à cocher font apparaître la BULK ACTION BAR — une barre
 *   d'actions sur une sélection, PAS la Save Bar (deux patterns distincts).
 *   Chaque action groupée reste protégée : session + user.manage + step-up
 *   + CSRF + transaction + invariant Super Admin + pas d'auto-désactivation.
 * · MODÈLES DE DROITS (08/10/2026) : les rôles d'un compte sont ses
 *   « modèles ». Appliquer un modèle qui accorde un droit critique exige un
 *   motif (le serveur répond REASON_REQUIRED, l'écran demande alors le
 *   motif) ; un modèle contenant des droits que l'administrateur n'a pas
 *   lui-même est refusé par le serveur, message compris.
 */

interface UserRow {
  id: string; email: string; display_name: string; status: string
  mfa_enabled: boolean; mfa_required: boolean
  roles: { key: string; name: string }[]
  last_login_at: string | null; active_sessions: number; created_at: string
}
interface RoleInfo { key: string; name: string }

function fmt(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function statutPill(status: string) {
  if (status === 'ACTIVE') return <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok" />Actif</span>
  if (status === 'SUSPENDED') return <span className="lc-pill lc-pill--warning">Activation à finaliser</span>
  return <span className="lc-pill lc-pill--error">Désactivé</span>
}

function UsersInner() {
  const toast = useToast()
  const { permissions, authenticated } = usePermissions()
  const canManage = authenticated && permissions.includes('user.manage')

  const [data, setData] = useState<{ total: number; items: UserRow[] } | null>(null)
  const [roles, setRoles] = useState<RoleInfo[]>([])
  const [creation, setCreation] = useState(false)
  const [creationMounted, setCreationMounted] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined)
  const [q, setQ] = useState('')

  const charger = useCallback(() => {
    apiGet<{ total: number; items: UserRow[] }>(`/api/v1/admin/users?${q ? `q=${encodeURIComponent(q)}` : ''}`)
      .then(setData).catch(() => undefined)
  }, [q])
  useEffect(() => { charger() }, [charger])
  useEffect(() => {
    apiGet<{ roles: { key: string; name: string }[] }>('/api/v1/admin/roles')
      .then((j) => setRoles(j.roles.map((r) => ({ key: r.key, name: r.name }))))
      .catch(() => undefined)
  }, [])

  const items = data?.items ?? []
  const allChecked = items.length > 0 && items.every((u) => selected.has(u.id))
  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(items.map((u) => u.id)))
  }
  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id); else next.add(id)
    setSelected(next)
  }

  const afterBulk = (applied: number, skipped: { id: string; reason: string }[], verb: string) => {
    if (applied > 0) toast('ok', `${verb} : ${applied} compte${applied > 1 ? 's' : ''} traité${applied > 1 ? 's' : ''}.`)
    for (const s of skipped.slice(0, 3)) {
      const u = items.find((x) => x.id === s.id)
      toast('error', `${u?.display_name ?? s.id} : ${s.reason}`)
    }
    if (skipped.length > 3) toast('error', `… et ${skipped.length - 3} autre(s) compte(s) non traité(s).`)
    setSelected(new Set())
    charger()
  }

  return (
    <main className="lc-page lc-stagger">
      <SettingsCrumb />
      <h1 className="lc-title">Utilisateurs</h1>
      <p className="lc-subtitle">Les comptes du portail Gestion — leurs modèles de droits, leur MFA, leurs sessions.</p>

      <div className="ad-toolbar">
        <div className="lc-search">
          <Search size={18} className="lc-search__icon" />
          <input placeholder="Rechercher un compte…" aria-label="Recherche utilisateurs"
            onChange={(e) => {
              clearTimeout(debounce.current)
              const v = e.target.value
              debounce.current = setTimeout(() => setQ(v), 350)
            }} />
        </div>
        {canManage ? (
          <button className="lc-btn lc-btn--primary" onClick={() => { setCreation(true); setCreationMounted(true) }}>
            <UserPlus size={15} /> Nouveau compte
          </button>
        ) : null}
        <span className="ad-count">{data ? `${data.total} compte${data.total > 1 ? 's' : ''}` : '…'}</span>
      </div>

      <section className="lc-panel gd-panel">
        <div className="lc-panel__body lc-panel__body--flush" style={{ overflowX: 'auto' }}>
          <table className="lc-table lc-table--selectable">
            <thead>
              <tr>
                {canManage ? (
                  <th className="lc-table__check">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll}
                      aria-label="Tout sélectionner sur la page" />
                  </th>
                ) : null}
                <th>Compte</th><th>État</th><th>MFA</th><th>Modèles</th><th>Dernière connexion</th><th>Sessions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id}
                  className={selected.has(u.id) ? 'lc-table__row--selected' : undefined}
                  onClick={() => navigate(`/settings/users/${u.id}`)} style={{ cursor: 'pointer' }}>
                  {canManage ? (
                    <td className="lc-table__check" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)}
                        aria-label={`Sélectionner ${u.display_name}`} />
                    </td>
                  ) : null}
                  <td>
                    <span className="lc-cell-name">
                      <span className="lc-avatar" style={{ background: 'var(--grad-accent)' }}>
                        {u.display_name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                      <span>
                        <div>{u.display_name}</div>
                        <div className="ad-mono" style={{ color: 'var(--text-3)' }}>{u.email}</div>
                      </span>
                    </span>
                  </td>
                  <td>{statutPill(u.status)}</td>
                  <td>{u.mfa_enabled
                    ? <span className="lc-pill lc-pill--ok"><ShieldCheck size={13} />TOTP</span>
                    : <span className="lc-pill lc-pill--idle">{u.mfa_required ? 'À activer' : 'Non requis'}</span>}</td>
                  <td>{u.roles.map((r) => r.name).join(', ') || '—'}</td>
                  <td className="ad-mono">{fmt(u.last_login_at)}</td>
                  <td>{u.active_sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {canManage && selected.size > 0 ? (
        <BulkBar
          count={selected.size}
          roles={roles}
          onClear={() => setSelected(new Set())}
          run={async (action, role, verb, reason) => {
            const r = await adminMutate<{ ok?: boolean; applied?: number; skipped?: { id: string; reason: string }[]; message?: string; error?: string }>(
              'POST', '/api/v1/admin/users/bulk',
              role ? { action, ids: [...selected], role, ...(reason ? { reason } : {}) } : { action, ids: [...selected] },
            )
            if (r.status === 200) { afterBulk(r.data.applied ?? 0, r.data.skipped ?? [], verb); return null }
            // Un modèle aux droits critiques : la modale redemande, motif compris.
            if (r.data.error === 'REASON_REQUIRED') return r.data.message ?? 'Un motif est obligatoire.'
            toast('error', r.data.message ?? 'Action groupée refusée — rien n’a changé.')
            return null
          }}
        />
      ) : null}

      {creationMounted ? (
        <CreateUserModal roles={roles} open={creation}
          onClose={() => setCreation(false)}
          onExited={() => setCreationMounted(false)}
          onCreated={() => {
            setCreation(false)
            toast('ok', 'Compte créé — activation à finaliser')
            charger()
          }} />
      ) : null}
    </main>
  )
}

/* ═══════════════ BULK ACTION BAR (≠ Save Bar) ═══════════════ */

function BulkBar(props: {
  count: number
  roles: RoleInfo[]
  onClear: () => void
  /** Rend un message quand le serveur exige un motif (la modale reste ouverte). */
  run: (action: string, role: string | undefined, verb: string, reason?: string) => Promise<string | null>
}) {
  const [roleAction, setRoleAction] = useState<'add_role' | 'remove_role' | null>(null)
  const [role, setRole] = useState('')
  const [reason, setReason] = useState('')
  const [reasonAsked, setReasonAsked] = useState<string | null>(null)
  const ouvrirModele = (a: 'add_role' | 'remove_role') => { setRoleAction(a); setRole(props.roles[0]?.key ?? ''); setReason(''); setReasonAsked(null) }
  const [confirm, setConfirm] = useState<{ action: string; verb: string; text: string } | null>(null)

  return createPortal(
    <>
      <div className="lc-bulkbar-zone">
        <div className="lc-bulkbar" role="toolbar" aria-label="Actions sur la sélection">
          <span className="lc-bulkbar__count">
            <span className="lc-dot" /> {props.count} utilisateur{props.count > 1 ? 's' : ''} sélectionné{props.count > 1 ? 's' : ''}
          </span>
          <span className="lc-bulkbar__actions">
            <button className="lc-btn" onClick={() => ouvrirModele('add_role')}>
              <KeyRound size={14} /> Appliquer un modèle
            </button>
            <button className="lc-btn" onClick={() => ouvrirModele('remove_role')}>
              Retirer un modèle
            </button>
            <button className="lc-btn" onClick={() => setConfirm({ action: 'enable', verb: 'Réactivation', text: 'Les comptes désactivés de la sélection seront réactivés.' })}>
              <UserCheck size={14} /> Activer
            </button>
            <button className="lc-btn" onClick={() => setConfirm({ action: 'disable', verb: 'Désactivation', text: 'Les comptes sélectionnés seront désactivés et TOUTES leurs sessions révoquées. Votre propre compte et le dernier Super Admin actif sont protégés.' })}>
              <UserX size={14} /> Désactiver
            </button>
            <button className="lc-btn" onClick={() => setConfirm({ action: 'revoke_sessions', verb: 'Révocation des sessions', text: 'Toutes les sessions actives des comptes sélectionnés seront révoquées immédiatement.' })}>
              <LogOut size={14} /> Révoquer les sessions
            </button>
          </span>
          <button className="lc-btn lc-btn--ghost lc-bulkbar__clear" onClick={props.onClear} aria-label="Vider la sélection">
            <X size={15} />
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirm !== null}
        danger={confirm?.action === 'disable' || confirm?.action === 'revoke_sessions'}
        title={`${confirm?.verb ?? ''} de ${props.count} compte${props.count > 1 ? 's' : ''} ?`}
        confirmLabel="Confirmer"
        onConfirm={() => {
          const c = confirm
          setConfirm(null)
          if (c) void props.run(c.action, undefined, c.verb)
        }}
        onClose={() => setConfirm(null)}
      >
        {confirm?.text}
      </ConfirmDialog>

      {roleAction ? (
        <Modal open onClose={() => setRoleAction(null)} ariaLabel="Choisir le modèle" width={480}>
          <header className="lc-modal__head">
            <span className="lc-modal__icon"><KeyRound size={20} /></span>
            <div className="lc-modal__title">{roleAction === 'add_role' ? 'Appliquer un modèle' : 'Retirer un modèle'}</div>
          </header>
          <div className="lc-modal__body">
            <p className="lc-card__desc" style={{ marginBottom: 'var(--sp-3)' }}>
              {roleAction === 'add_role'
                ? `Les ${props.count} compte(s) sélectionné(s) recevront aussi les droits de ce modèle. Vous ne pouvez appliquer que des droits que vous détenez vous-même.`
                : `Les ${props.count} compte(s) sélectionné(s) perdront les droits que seul ce modèle leur donnait — le dernier Super Admin actif est protégé.`}
            </p>
            <Select value={role} onChange={(v) => { setRole(v); setReasonAsked(null) }} ariaLabel="Modèle"
              options={props.roles.map((r) => ({ value: r.key, label: r.name }))} />
            {reasonAsked !== null ? (
              <div style={{ marginTop: 'var(--sp-3)' }}>
                <Field label="Motif" helper={reasonAsked}>
                  <TextInput value={reason} onChange={setReason} maxLength={500} placeholder="Pourquoi ce changement ?" ariaLabel="Motif" />
                </Field>
              </div>
            ) : null}
            <div className="lc-dialog__actions">
              <button className="lc-btn lc-btn--ghost" onClick={() => setRoleAction(null)}>Annuler</button>
              <button className="lc-btn lc-btn--primary" disabled={!role || (reasonAsked !== null && !reason.trim())} onClick={() => {
                const a = roleAction
                void props.run(a, role, a === 'add_role' ? 'Application du modèle' : 'Retrait du modèle', reason.trim() || undefined).then((ask) => {
                  if (ask) setReasonAsked(ask)
                  else setRoleAction(null)
                })
              }}>
                {roleAction === 'add_role' ? 'Appliquer' : 'Retirer'}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>,
    document.body,
  )
}

/* ═══════════════ CRÉATION (modale + Save Bar « Créer le compte ») ═══════════════ */

function CreateUserModal(props: {
  roles: RoleInfo[]
  open: boolean
  onClose: () => void
  onExited: () => void
  onCreated: () => void
}) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [display, setDisplay] = useState('')
  const [displayTouched, setDisplayTouched] = useState(false)
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [sel, setSel] = useState<string[]>([])
  // Motif : demandé seulement quand le serveur l'exige (modèle aux droits critiques).
  const [reasonAsked, setReasonAsked] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  // Nom affiché suggéré depuis prénom + nom, tant qu'il n'a pas été édité.
  const effectiveDisplay = displayTouched ? display : [firstName, lastName].filter(Boolean).join(' ')

  const dirty = !!(firstName || lastName || display || email || phone || sel.length)
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  useSaveBar({
    dirty,
    saveLabel: 'Créer le compte',
    invalid: !emailOk ? 'Une adresse e-mail valide est requise.'
      : effectiveDisplay.trim().length < 2 ? 'Le nom affiché doit faire au moins 2 caractères.'
        : reasonAsked !== null && !reason.trim() ? 'Indiquez le motif : ces modèles accordent des droits critiques.' : null,
    successMessage: 'Compte créé — activation à finaliser',
    onSave: async () => {
      const body: Record<string, unknown> = {
        email: email.trim(), display_name: effectiveDisplay.trim(), roles: sel,
      }
      if (firstName.trim()) body['first_name'] = firstName.trim()
      if (lastName.trim()) body['last_name'] = lastName.trim()
      if (phone.trim()) body['phone'] = phone.trim()
      if (reason.trim()) body['reason'] = reason.trim()
      const r = await adminMutate<{ id?: string; error?: string; message?: string }>('POST', '/api/v1/admin/users', body)
      if (r.status === 400 && r.data.error === 'REASON_REQUIRED') {
        setReasonAsked(r.data.message ?? 'Un motif est obligatoire.')
        return 'Indiquez le motif sous les modèles, puis créez le compte.'
      }
      if (r.status !== 201) return r.data.message ?? 'Le compte n’a pas été créé.'
      props.onCreated()
      return true
    },
    onCancel: () => {
      setFirstName(''); setLastName(''); setDisplay(''); setDisplayTouched(false)
      setEmail(''); setPhone(''); setSel([]); setReason(''); setReasonAsked(null)
    },
  })

  const close = () => guardedClose(dirty, props.onClose)

  return (
    <Modal open={props.open} onClose={close} onExited={props.onExited} ariaLabel="Nouveau compte" width={720} className="lc-modal--editable">
      <header className="lc-modal__head">
        <span className="lc-modal__icon"><UserPlus size={20} /></span>
        <div>
          <div className="lc-modal__title">Nouveau compte</div>
          <div className="lc-modal__pills">
            <span className="lc-pill lc-pill--warning">Activation à finaliser après création</span>
          </div>
        </div>
        <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={close} aria-label="Fermer">
          <X size={18} />
        </button>
      </header>
      <div className="lc-modal__body">
        <div className="adm-note" style={{ marginTop: 0 }}>
          Aucun mot de passe n’est choisi ici : la personne définira le sien
          lors de l’activation (flux d’invitation à venir, procédure au runbook
          en attendant). Le MFA sera requis à sa première connexion.
        </div>
        <div className="rm-fields" style={{ marginTop: 'var(--sp-4)' }}>
          <div className="lc-field">
            <label className="lc-label" htmlFor="nu-first">Prénom</label>
            <input id="nu-first" className="lc-input" value={firstName} maxLength={80} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor="nu-last">Nom</label>
            <input id="nu-last" className="lc-input" value={lastName} maxLength={80} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor="nu-display">Nom affiché</label>
            <input id="nu-display" className="lc-input" value={effectiveDisplay} maxLength={120}
              onChange={(e) => { setDisplay(e.target.value); setDisplayTouched(true) }} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor="nu-email">Adresse e-mail</label>
            <input id="nu-email" className="lc-input" type="email" value={email} maxLength={320} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="lc-field">
            <label className="lc-label" htmlFor="nu-phone">Téléphone <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optionnel)</span></label>
            <input id="nu-phone" className="lc-input" type="tel" value={phone} maxLength={30} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>
        <div className="ap-group">Modèles de droits</div>
        <div className="ad-perm-grid">
          {props.roles.map((r) => (
            <div key={r.key} className="ad-perm">
              <Checkbox checked={sel.includes(r.key)} label={r.name}
                onChange={(on) => { setSel(on ? [...sel, r.key] : sel.filter((k) => k !== r.key)); setReasonAsked(null) }} />
            </div>
          ))}
        </div>
        {reasonAsked !== null ? (
          <div style={{ marginTop: 'var(--sp-3)' }}>
            <Field label="Motif" helper={reasonAsked}>
              <TextInput value={reason} onChange={setReason} maxLength={500} placeholder="Pourquoi ces droits ?" ariaLabel="Motif" />
            </Field>
          </div>
        ) : null}
        <p className="lc-form__help" style={{ marginTop: 'var(--sp-3)' }}>
          <Fingerprint size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
          La personne pourra ajouter une clé d’accès (passkey) ou un TOTP depuis son profil.
        </p>
      </div>
    </Modal>
  )
}

export function UsersPage() {
  return <ToastProvider><UsersInner /></ToastProvider>
}
